import { Button, Flex, Heading, IconArrowStartLine } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { clear, get } from "idb-keyval";
import { useContext, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router";
import {
  ERROR,
  useNotification,
} from "../../../../components/CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../../components/DialogYesNo/DialogYesNo";
import { ActionService } from "../../../../services/ActionService";
import { AssignmentService } from "../../../../services/AssignmentService";
import { Files } from "../../../../services/FilesService";
import { ReflectionService } from "../../../../services/ReflectionService";
import { UserDetailContext } from "../../../../utils/Context";
import {
  csvFileEnricher,
  fileReader,
  fileWriter,
} from "../../../../utils/FileUtils";
import FinalizeVerifyModal from "./FinalizeVerifyModal/FinalizeVerifyModal";
import {
  ASSIGNMENT,
  enrichResults,
  LMG,
  RESULTS_FILE_TYPE,
  UPLOAD,
} from "./utils";

const FinalizeSectionResults = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const { section, reflectionTemplates } = location.state;
  const offering = section.offering;
  const path = location.pathname;

  // formData.metadata structure based on type:
  // ASSIGNMENT: { assignmentId: number, assignmentName: string, results: object }
  // LMG: { roundingMethod: string, results: object }
  // UPLOAD: { assignmentName: string }
  const [formData, setFormData] = useState(
    sessionStorage.getItem("finalization/" + section.canvas_id)
      ? JSON.parse(sessionStorage.getItem("finalization/" + section.canvas_id))
      : { type: "", metadata: {} }
  );

  function initializeNextEnabled() {
    if (formData.type === ASSIGNMENT) {
      return !!formData.metadata.results;
    } else if (formData.type === LMG) {
      const hasResults = !!formData.metadata.results;
      const hasRounding = !!formData.metadata.roundingMethod;
      const outcomes = formData.metadata.outcomeMetadata;
      const scaleMap = formData.metadata.outcomeRatingScaleMap || {};
      const allScalesSelected =
        !outcomes || Object.keys(outcomes).every((id) => !!scaleMap[id]);
      return hasResults && hasRounding && allScalesSelected;
    } else if (formData.type === UPLOAD) {
      return !!formData.metadata.assignmentName;
    }
    return false;
  }

  const [nextEnabled, setNextEnabled] = useState(initializeNextEnabled());
  const [nextPath, setNextPath] = useState("");
  const [previousPath, setPreviousPath] = useState("");
  const [confirmation, setConfirmation] = useState(false);

  const [assignments, setAssignments] = useState([]);
  const [reflections, setReflections] = useState(null);
  const [actions, setActions] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);
  const [openVerify, setOpenVerify] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(
      "finalization/" + section.canvas_id,
      JSON.stringify(formData)
    );
  }, [formData, section.canvas_id]);

  useEffect(() => {
    async function fetchAssignments() {
      try {
        // Fetch assignments for Canvas course sections
        let data = await AssignmentService.getAssignmentsBySectionId(
          section.canvas_id
        );
        data = data.map((assignment) => ({
          ...assignment,
          label: assignment.name,
        }));
        setAssignments(data);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("assignment.assignments"),
          }),
          ERROR
        );
      }
    }
    fetchAssignments();
  }, [section.canvas_id, showNotification]);

  useEffect(() => {
    async function fetchReflections() {
      try {
        const fetchedReflections =
          await ReflectionService.getReflectionsForContext(
            section.id,
            userDetails.canvas_id
          );

        const reflectionList = [];
        for (const reflection of fetchedReflections) {
          if (reflection.from_template) {
            if (!reflectionList.some((r) => r.ddl_id === reflection.ddl_id)) {
              const groupedReflections = fetchedReflections.filter(
                (r) => r.ddl_id === reflection.ddl_id
              );
              const template = reflectionTemplates.find(
                (t) => t.id === reflection.template_id
              );

              const templateReflection = {
                ...reflection,
                sections: template.sections.map((section, i) => ({
                  id: section.id,
                  content: groupedReflections.find(
                    (r) => r.section_id === section.id
                  ).content,
                  header_question: section.header_question,
                  helper_text: template.sections[i].helper_text,
                })),
              };
              reflectionList.push(templateReflection);
            }
          } else {
            reflectionList.push(reflection);
          }
        }

        reflectionList.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        setReflections(reflectionList);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("reflections.reflections"),
          }),
          ERROR
        );
      }
    }
    if (userDetails.ddl_id && !reflections) {
      fetchReflections();
    }
  }, [
    reflections,
    section,
    reflectionTemplates,
    userDetails,
    showNotification,
  ]);

  useEffect(() => {
    async function fetchActions() {
      try {
        const [fetchedActions, fetchedActionTypes] = await Promise.all([
          ActionService.getActionsForContext(section.id),
          ActionService.getActionTypes(),
        ]);
        setActions(fetchedActions);
        setActionTypes(
          fetchedActionTypes.map((a) => ({ ...a, label: a.name }))
        );
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: t("actions.actions") }),
          ERROR
        );
      }
    }
    if (!actions) {
      fetchActions();
    }
  }, [actions, section, showNotification]);

  useEffect(() => {
    if (path.includes("/scores/home")) {
      setPreviousPath(`/courses?courseId=${section.canvas_id}`);
      setNextPath("/scores/reflections");
    } else if (path === "/scores/reflections") {
      if (formData.type === ASSIGNMENT) {
        setPreviousPath("/scores/home/assignment");
      } else if (formData.type === LMG) {
        setPreviousPath("/scores/home/lmg");
      } else if (formData.type === UPLOAD) {
        setPreviousPath("/scores/home/upload");
      } else {
        setPreviousPath("/scores/home");
      }
      setNextPath("/scores/actions");
    } else if (path === "/scores/actions") {
      setPreviousPath("/scores/reflections");
      setNextPath("/scores/summary");
    } else if (path === "/scores/summary") {
      setPreviousPath("/scores/actions");
      setNextPath("");
    }
  }, [path, section.canvas_id, formData.type, formData.metadata.results]);

  async function handleSubmit() {
    try {
      if (formData.type === ASSIGNMENT) {
        await AssignmentService.finalizeResultsByContext({
          mode: AssignmentService.ASSIGNMENT,
          contextId: formData.metadata.assignmentId,
          data: JSON.stringify(formData.metadata.results.data),
        });
      } else if (formData.type === LMG) {
        await AssignmentService.finalizeResultsByContext({
          mode: AssignmentService.ROLLUP,
          contextId: section.canvas_id,
          roundingMode: formData.metadata.roundingMethod,
          data: JSON.stringify(formData.metadata.results.data),
          outcomeRatingScaleMap: formData.metadata.outcomeRatingScaleMap,
        });
      } else if (formData.type === UPLOAD) {
        const resultsFile = await get("uploadFile");
        if (resultsFile) {
          const fr = await fileReader(resultsFile);
          const content = fr.result;
          const enrichedContent = csvFileEnricher(
            content,
            enrichResults(section.id, section.term.canvas_id)
          );
          const enrichedFile = fileWriter(
            enrichedContent,
            resultsFile.name,
            resultsFile.type
          );

          await Files.saveFile(enrichedFile, RESULTS_FILE_TYPE);
        } else {
          throw new Error("No file found to upload");
        }
      } else {
        throw new Error("Invalid finalization type");
      }
      setConfirmation(false);
      setOpenVerify(true);
    } catch (error) {
      if (error?.status === 409) {
        showNotification(t("finalizeCourse.ratingScaleLocked"), ERROR);
      } else {
        showNotification(
          t("finalizeCourse.finalizeError", {
            course: offering.parent_course_code,
          }),
          ERROR
        );
      }
    }
  }

  return (
    <div
      style={{
        height: "calc(-3rem + 100vh)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Heading as="h1" level="h2" border="bottom">
        {offering.parent_course_code}
      </Heading>
      <div style={{ flexGrow: 1, overflowY: "auto", padding: "0.75rem 0" }}>
        <Outlet
          context={{
            setNextEnabled,
            formData,
            setFormData,
            assignments,
            reflections,
            setReflections,
            actions,
            setActions,
            actionTypes,
            setActionTypes,
          }}
        />
      </div>
      <Flex gap="small" wrap="wrap">
        <Flex.Item width="100%">
          <div style={{ borderBottom: "1px solid #ccc" }} />
        </Flex.Item>
        <Flex.Item shouldGrow>
          {path.includes("/scores/home") ? (
            <Link to={previousPath}>
              <Button
                renderIcon={IconArrowStartLine}
                onClick={async () => {
                  sessionStorage.clear();
                  await clear();
                }}
              >
                {t("global.goBack")}
              </Button>
            </Link>
          ) : (
            <Link to={previousPath} state={location.state}>
              <Button>{t("global.previous")}</Button>
            </Link>
          )}
        </Flex.Item>
        <Flex.Item>
          {path === "/scores/summary" ? (
            <>
              <Button color="primary" onClick={() => setConfirmation(true)}>
                {t("global.submit")}
              </Button>
              <DialogYesNo
                message={t("finalizeCourse.finalizeConfirm", {
                  course: offering.parent_course_code,
                })}
                openStatus={confirmation}
                onClickNo={() => setConfirmation(false)}
                onClickYes={handleSubmit}
              />
            </>
          ) : (
            <Link to={nextPath} state={location.state}>
              <Button color="primary" disabled={!nextEnabled}>
                {t("global.next")}
              </Button>
            </Link>
          )}
        </Flex.Item>
      </Flex>
      <FinalizeVerifyModal
        open={openVerify}
        setOpen={setOpenVerify}
        courseCode={offering.parent_course_code}
        sectionCanvasId={section.canvas_id}
        resubmitFunction={handleSubmit}
      />
    </div>
  );
};

export default FinalizeSectionResults;
